<template>
  <div class="manual-allocation-view">
    <navbar :hasSearch="false" />

    <!-- Edit mode -->
    <template v-if="isManualAllocationsEdit">
      <div v-if="loading" class="text-center mt-5">
        <b-spinner variant="primary" style="width: 3rem; height: 3rem" />
      </div>

      <div v-else-if="fatalError" class="fatal-error p-4">
        <b-alert variant="danger" show>
          <h5>Unable to Load</h5>
          <p>The allocation edit view could not be loaded. Please try again or contact support.</p>
        </b-alert>
      </div>

      <div v-else id="content">
        <div class="allocation-content" id="edit-content">
        <div class="row mb-3">
          <div class="col-12">
            <div class="card h-100">
              <ol class="breadcrumb my-auto">
                <template v-for="(item, idx) in editBreadcrumbItems">
                  <li v-if="!item.active" :key="'e-' + idx" class="breadcrumb-item">
                    <router-link :to="item.to">{{ item.text }}</router-link>
                    <i class="fa fa-angle-right mx-2" aria-hidden="true"></i>
                  </li>
                  <li v-else :key="'ea-' + idx" class="breadcrumb-item active font-weight-bold">
                    {{ item.text }}
                  </li>
                </template>
              </ol>
            </div>
          </div>
        </div>

        <div class="row mb-3">
          <div class="col-12">
            <div class="card h-100 py-2 px-3">
              <h5 class="mb-0 font-weight-bold">Manual Allocation Dashboard</h5>
            </div>
          </div>
        </div>

        <b-alert
          v-if="error"
          variant="danger"
          show
          dismissible
          class="allocation-error-alert"
          @dismissed="$store.commit('setError', null)"
        >{{ error }}</b-alert>

        <div v-if="transaction" class="row mb-4">
          <div class="col-md-4">
            <div class="card h-100 p-3">
              <div class="mb-2">
                <span class="manual-allocation-summary-icon">
                  <i class="fas fa-file-alt"></i>
                </span>
              </div>
              <div class="text-muted small mb-1">{{ transaction.Type }}</div>
              <div class="font-weight-bold" style="font-size:1.25rem;">{{ transaction.Reference || transaction.Id }}</div>
              <div class="text-muted small mt-1">Issued: {{ formatDDMMYYYY(transaction.Date) }}</div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="card h-100 p-3">
              <div class="mb-2">
                <span class="manual-allocation-summary-icon">
                  <i class="fas fa-receipt"></i>
                </span>
              </div>
              <div class="text-muted small mb-1">Amount</div>
              <div class="font-weight-bold" style="font-size:1.25rem;">{{ transaction.Value | currency }}</div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="card h-100 p-3">
              <div class="mb-2">
                <span class="manual-allocation-summary-icon">
                  <i class="far fa-check-circle"></i>
                </span>
              </div>
              <div class="text-muted small mb-1">Allocated</div>
              <div class="font-weight-bold" style="font-size:1.25rem;">{{ transaction.Allocated | currency }}</div>
            </div>
          </div>
        </div>

        <div v-if="editAvailableTabs && editAvailableTabs.length > 0" class="row mb-3">
          <div class="col-12">
            <ul class="nav allocation-tabs px-0 pt-1">
              <li class="nav-item" v-for="type in editAvailableTabs" :key="'et-' + type">
                <a
                  class="nav-link allocation-tab-link pb-2"
                  :class="{ 'active': activeTab === type }"
                  href="#"
                  @click.prevent="onEditTypeSelect(type)"
                >{{ getTypeSingularLabel(type) }}</a>
              </li>
            </ul>
          </div>
        </div>

        <div class="card mb-3">
          <div class="filter-panel p-3 border-bottom">
            <b-row align-v="end">
              <b-col md="2">
                <b-form-group label="Reference" class="mb-0" label-class="small text-muted">
                  <b-form-input v-model="filterDraft.reference" placeholder="Search..." size="sm" />
                </b-form-group>
              </b-col>
              <b-col md="2">
                <b-form-group label="Date" class="mb-0" label-class="small text-muted">
                  <el-date-picker
                    v-model="filterDraft.date"
                    type="date"
                    placeholder="Date"
                    size="small"
                    style="width: 100%"
                  />
                </b-form-group>
              </b-col>
              <b-col md="3">
                <b-form-group label="Amount Range" class="mb-0" label-class="small text-muted">
                  <div class="d-flex align-items-center">
                    <b-form-input v-model.number="filterDraft.amountMin" type="number" placeholder="Min" size="sm" />
                    <span class="mx-1 text-muted">–</span>
                    <b-form-input v-model.number="filterDraft.amountMax" type="number" placeholder="Max" size="sm" />
                  </div>
                </b-form-group>
              </b-col>
              <b-col md="3">
                <b-form-group label="Remaining Range" class="mb-0" label-class="small text-muted">
                  <div class="d-flex align-items-center">
                    <b-form-input v-model.number="filterDraft.remainingMin" type="number" placeholder="Min" size="sm" />
                    <span class="mx-1 text-muted">–</span>
                    <b-form-input v-model.number="filterDraft.remainingMax" type="number" placeholder="Max" size="sm" />
                  </div>
                </b-form-group>
              </b-col>
            </b-row>
          </div>
          <div class="p-2 d-flex justify-content-end">
            <b-button variant="outline-secondary" class="mr-2" @click="clearEditFilters">Clear Filters</b-button>
            <b-button variant="primary" :disabled="!hasFilterDraft" @click="applyEditFilters">Apply Filters</b-button>
          </div>
        </div>

        <div class="d-flex justify-content-between align-items-center mb-2 px-0">
          <small class="text-muted">{{ editShowingText }}</small>
          <div class="tab-pagination-wrapper" v-if="filteredAllocations.length > 0">
            <b-pagination
              :value="editPage"
              :total-rows="filteredAllocations.length"
              :per-page="editPageSize"
              :limit="7"
              size="sm"
              class="mb-0"
              hide-goto-end-buttons
              prev-text="Prev"
              next-text="Next"
              @change="onEditPageChange"
            />
          </div>
        </div>

        <schema-table
          :schema="editAllocationsSchema"
          :data="pagedEditAllocations"
          :loading="undoing"
          empty-text="No allocated items match your filters"
          @action-click="onUndoRowAction"
        />

        <div class="d-flex justify-content-end mt-3">
          <b-button variant="outline-secondary" @click="onEditCancel">Cancel</b-button>
        </div>

          <b-modal
            id="undo-allocation-modal"
            title="Undo Allocation"
            centered
            hide-footer
          >
            <p>Are you sure you want to undo this allocation?</p>
            <div class="d-flex justify-content-end">
              <b-button variant="outline-secondary" class="mr-2" @click="$bvModal.hide('undo-allocation-modal')">No</b-button>
              <b-button variant="danger" :disabled="undoing" @click="confirmUndoAllocation">Yes</b-button>
            </div>
          </b-modal>
        </div>
      </div>
    </template>

    <!-- Loading state -->
    <template v-else>
    <div v-if="loading" class="text-center mt-5">
      <b-spinner variant="primary" style="width: 3rem; height: 3rem" />
    </div>

    <!-- Fatal error state — hides all content -->
    <div v-else-if="fatalError" class="fatal-error p-4">
      <b-alert variant="danger" show>
        <h5>Unable to Load</h5>
        <p>The allocation view could not be loaded. Please try again or contact support.</p>
      </b-alert>
    </div>

    <!-- Main content -->
    <div v-else class="allocation-content" id="content">

      <!-- Breadcrumb -->
      <div class="row mb-3">
        <div class="col-12">
          <div class="card h-100">
            <ol class="breadcrumb my-auto">
              <template v-for="(item, idx) in breadcrumbItems">
                <li v-if="!item.active" :key="idx" class="breadcrumb-item">
                  <router-link :to="item.to"><a class="black-text" href="#">{{ item.text }}</a></router-link>
                  <i class="fa fa-angle-right mx-2" aria-hidden="true"></i>
                </li>
                <li v-else :key="'a-' + idx" class="breadcrumb-item active font-weight-bold">
                  <a class="black-text" href="#">{{ item.text }}</a>
                </li>
              </template>
            </ol>
          </div>
        </div>
      </div>

      <!-- Page header card -->
      <div class="row mb-3">
        <div class="col-12">
          <div class="card h-100 py-2 px-3 d-flex flex-row justify-content-between align-items-center">
            <h5 class="mb-0 font-weight-bold">Manual Allocation</h5>
            <b-button variant="link" class="p-0 text-dark" @click="onClose" aria-label="Close">
              <i class="fas fa-times"></i>
            </b-button>
          </div>
        </div>
      </div>

      <!-- Inline error banner (non-fatal) -->
      <b-alert
        v-if="error"
        variant="danger"
        show
        dismissible
        class="allocation-error-alert"
        @dismissed="$store.commit('setError', null)"
      >{{ error }}</b-alert>

      <!-- Transaction Details Panel -->
      <div v-if="transaction" class="row mb-4">
        <!-- Card 1: Reference -->
        <div class="col-md-4">
          <div class="card h-100 p-3">
            <div class="mb-2">
              <span class="manual-allocation-summary-icon">
                <i class="fas fa-file-alt"></i>
              </span>
            </div>
            <div class="text-muted small mb-1">{{ transaction.Type }}</div>
            <div class="font-weight-bold" style="font-size:1.25rem;">{{ transaction.Reference }}</div>
            <div class="text-muted small mt-1">Issued: {{ formatDDMMYYYY(transaction.Date) }} Reference: {{ transaction.Reference }} Type: {{ transaction.Type }}</div>
          </div>
        </div>
        <!-- Card 2: Amount -->
        <div class="col-md-4">
          <div class="card h-100 p-3">
            <div class="mb-2">
              <span class="manual-allocation-summary-icon">
                <i class="fas fa-receipt"></i>
              </span>
            </div>
            <div class="text-muted small mb-1">Amount</div>
            <div class="font-weight-bold" style="font-size:1.25rem;">{{ transaction.Value | currency }}</div>
          </div>
        </div>
        <!-- Card 3: Remaining -->
        <div class="col-md-4">
          <div class="card h-100 p-3">
            <div class="mb-2">
              <span class="manual-allocation-summary-icon">
                <i class="fas fa-hourglass-half"></i>
              </span>
            </div>
            <div class="text-muted small mb-1">Remaining</div>
            <div class="font-weight-bold" style="font-size:1.25rem;">{{ liveTargetRemaining | currency }}</div>
          </div>
        </div>
      </div>

      <!-- Type selector tabs (full width, spans both columns) -->
      <div v-if="availableTypes && availableTypes.length > 0" class="row mb-0 allocation-tabs-row">
        <div class="col-12">
          <ul class="nav allocation-tabs px-0 pt-1">
            <li class="nav-item" v-for="type in availableTypes" :key="type">
              <a
                class="nav-link allocation-tab-link pb-2"
                :class="{ 'active': activeTab === type }"
                href="#"
                @click.prevent="onTypeSelect(type)"
              >{{ getTypeSingularLabel(type) }}</a>
            </li>
          </ul>
        </div>
      </div>

      <!-- Two-column layout -->
      <div class="row">

        <!-- Left column: filter controls + Available Items Table -->
        <div class="col-md-7">

          <!-- Type selector tabs (standalone row, transparent background) -->
          <template v-if="availableTypes && availableTypes.length > 0">

            <!-- Filter controls -->
            <div class="card mb-3">
              <div class="filter-panel p-3 border-bottom">
                <b-row align-v="end">
                  <b-col cols="auto" class="d-flex align-items-end pb-2 pr-0">
                    <i class="fas fa-filter text-muted"></i>
                  </b-col>
                  <b-col md="2">
                    <b-form-group label="Reference" class="mb-0" label-class="small text-muted">
                      <b-form-input v-model="filterDraft.reference" placeholder="Search..." size="sm" />
                    </b-form-group>
                  </b-col>
                  <b-col md="2">
                    <b-form-group label="Date" class="mb-0" label-class="small text-muted">
                      <el-date-picker
                        v-model="filterDraft.date"
                        type="date"
                        placeholder="Date"
                        size="small"
                        style="width: 100%"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col md="3">
                    <b-form-group label="Amount Range" class="mb-0" label-class="small text-muted">
                      <div class="d-flex align-items-center">
                        <b-form-input v-model.number="filterDraft.amountMin" type="number" placeholder="Min" size="sm" />
                        <span class="mx-1 text-muted">–</span>
                        <b-form-input v-model.number="filterDraft.amountMax" type="number" placeholder="Max" size="sm" />
                      </div>
                    </b-form-group>
                  </b-col>
                  <b-col md="3">
                    <b-form-group label="Remaining Range" class="mb-0" label-class="small text-muted">
                      <div class="d-flex align-items-center">
                        <b-form-input v-model.number="filterDraft.remainingMin" type="number" placeholder="Min" size="sm" />
                        <span class="mx-1 text-muted">–</span>
                        <b-form-input v-model.number="filterDraft.remainingMax" type="number" placeholder="Max" size="sm" />
                      </div>
                    </b-form-group>
                  </b-col>
                </b-row>
              </div>
              <div class="p-2 d-flex justify-content-end">
                <b-button variant="outline-secondary" class="mr-2" @click="clearFilters">Clear Filters</b-button>
                <b-button variant="primary" :disabled="!hasFilterDraft" @click="applyFilters">Apply Filters</b-button>
              </div>
            </div>

            <!-- Available Items Table -->
            <div v-if="tabTotalCount > 0" class="d-flex justify-content-between align-items-center mb-2 px-0">
              <small class="text-muted">{{ tabShowingText }}</small>
              <div class="tab-pagination-wrapper">
                <b-pagination
                  :value="tabCurrentPage"
                  :total-rows="tabTotalCount"
                  :per-page="tabPageSize"
                  :limit="7"
                  size="sm"
                  class="mb-0"
                  hide-goto-end-buttons
                  prev-text="Prev"
                  next-text="Next"
                  @change="onTabPageChange"
                />
              </div>
            </div>
            <schema-table
              :schema="availableItemsSchema"
              :data="availableItemsForDisplay"
              :loading="tabLoading"
              empty-text="No transactions available for this type"
              @action-click="onAvailableItemSelect"
              @sort-changed="onAvailableSortChanged"
            />
          </template>

          <!-- No allocations available state -->
          <div v-else class="no-allocations-available text-muted py-3">
            <p>No allocations are available for this transaction.</p>
          </div>

        </div>

        <!-- Right column: Allocate Amount Section + Allocated Items Table -->
        <div class="col-md-5">
          <div class="card p-3">

            <!-- Heading -->
            <h6 class="font-weight-bold mb-2">Allocation</h6>
            <hr class="mt-0 mb-3" />

            <!-- Allocate Amount Section -->
            <template v-if="selectedAvailableId || selectedAllocationId">

              <!-- Selected item info bubble -->
              <div v-if="selectedItem" class="rounded p-3 mb-3" style="background:#f4f6f9;">
                <div class="text-muted small mb-1">Selected {{ selectedItemContextLabel }}</div>
                <span class="font-weight-bold mr-2">{{ selectedItemDisplayReference }}</span>
                <span class="text-muted small">{{ selectedItem.Date | shortDate }}</span>
              </div>

              <!-- Amount + Remaining bubbles -->
              <div v-if="selectedItem" class="row mb-3">
                <div class="col-6">
                  <div class="rounded p-3" style="background:#f4f6f9;">
                    <div class="text-muted small mb-1">Amount</div>
                    <div class="font-weight-bold">{{ selectedItem.Value | currency }}</div>
                  </div>
                </div>
                <div class="col-6">
                  <div class="rounded p-3" style="background:#f4f6f9;">
                    <div class="text-muted small mb-1">Remaining</div>
                    <div class="font-weight-bold">{{ liveSourceRemaining(selectedAvailableId || selectedAllocationId) | currency }}</div>
                  </div>
                </div>
              </div>

              <!-- Amount input -->
              <label class="font-weight-bold small mb-1">Allocate Amount</label>
              <b-input-group class="mb-3">
                <b-form-input
                  type="number"
                  :value="allocateAmount"
                  :class="{ 'is-invalid': allocationError(allocateAmount) !== null }"
                  placeholder="Enter amount"
                  @input="onAmountInput"
                />
                <b-input-group-append>
                  <span class="input-group-text bg-white"><i class="fas fa-calculator text-muted"></i></span>
                </b-input-group-append>
                <div class="invalid-feedback" v-if="allocationError(allocateAmount)">
                  {{ allocationError(allocateAmount) }}
                </div>
              </b-input-group>

              <!-- Max Amount + Allocate full-width buttons -->
              <div class="d-flex mb-3" style="gap:0.5rem;">
                <b-button
                  variant="outline-secondary"
                  class="flex-fill"
                  :disabled="!selectedAvailableId && !selectedAllocationId || availableTypes.length === 0"
                  @click="onMaxAmount"
                >Max Amount</b-button>
                <b-button
                  variant="primary"
                  class="flex-fill"
                  :disabled="isAllocateDisabled(allocateAmount) || availableTypes.length === 0"
                  @click="onAllocate"
                >Allocate</b-button>
              </div>

            </template>

            <!-- Pending Allocations heading -->
            <h6 class="font-weight-bold mb-2">Pending Allocations</h6>
            <hr class="mt-0 mb-2" />

            <!-- Allocated Items Table -->
            <table class="table table-sm table-hover mb-3">
              <thead>
                <tr>
                  <th class="text-muted font-weight-normal small">Reference</th>
                  <th class="text-muted font-weight-normal small">Date</th>
                  <th class="text-muted font-weight-normal small">Amount</th>
                  <th class="text-muted font-weight-normal small text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="entry in allAllocations"
                  :key="'p-' + (entry.FromTransactionType || 'unknown') + '-' + entry.FromTransactionId"
                  :class="allocationRowClasses(entry)"
                  @click="onAllocatedItemSelect(entry)"
                >
                  <td>
                    <div class="font-weight-bold">{{ entry.fromReference }}</div>
                  </td>
                  <td>{{ entry.fromDate | shortDate }}</td>
                  <td>{{ Math.abs(Number(entry.Amount || 0)) | currency }}</td>
                  <td class="text-right">
                    <button
                      class="btn btn-link text-danger p-0"
                      @click.stop="onRemoveAllocation(entry)"
                    ><i class="fas fa-minus-circle"></i></button>
                  </td>
                </tr>
              </tbody>
            </table>

            <!-- Action buttons: right-aligned -->
            <div class="d-flex justify-content-end" style="gap:0.5rem;">
              <b-button
                variant="primary"
                :disabled="!hasPendingAllocations || saving || hasPendingInvalid"
                @click="$bvModal.show('save-allocation-modal')"
              >Save Allocation</b-button>
            </div>

          </div>
        </div>
      </div>

      <!-- Save Allocation Modal -->
      <b-modal
        id="save-allocation-modal"
        title="Save Allocation"
        centered
        no-close-on-backdrop
        hide-footer
        @keydown.native.enter="onModalSave"
      >
        <p>Are you sure you want to save all pending allocations? This action cannot be undone.</p>
        <div class="d-flex justify-content-end">
          <b-button variant="primary" @click="onModalSave">Save</b-button>
        </div>
      </b-modal>

    </div>
    </template>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { getTypeSingular } from '@/utils/transactionTypeUtils'
import SchemaTable from '@/components/common/SchemaTable.vue'
import Navbar from '@/components/Navbar.vue'
import filtersMixin from '@/mixins/filters'

export default {
  name: 'ManualAllocationView',

  mixins: [filtersMixin],

  components: {
    SchemaTable,
    Navbar
  },

  beforeRouteEnter: function (to, from, next) {
    next(function (vm) {
      if (to.name === 'manualAllocationsEdit') {
        vm.$store.dispatch('fetchEditAllocations', { transactionId: to.params.transactionId, transactionType: to.params.transactionType })
      } else {
        vm.$store.dispatch('fetchAllocationDialogViewModel', { transactionId: to.params.transactionId, transactionType: to.params.transactionType })
      }
    })
  },

  beforeRouteUpdate: function (to, from, next) {
    if (to.name === 'manualAllocationsEdit') {
      this.$store.dispatch('fetchEditAllocations', { transactionId: to.params.transactionId, transactionType: to.params.transactionType })
    } else {
      this.$store.dispatch('fetchAllocationDialogViewModel', { transactionId: to.params.transactionId, transactionType: to.params.transactionType })
    }
    next()
  },

  data: function () {
    return {
      filterDraft: {
        reference: '',
        date: null,
        amountMin: null,
        amountMax: null,
        remainingMin: null,
        remainingMax: null
      },
      editPage: 1,
      undoCandidate: null
    }
  },

  computed: Object.assign(
    {},
    mapState({
      transaction: function (state) { return state.allocationDialog.transaction },
      availableTypes: function (state) { return state.allocationDialog.availableTypes },
      allocations: function (state) { return state.allocationDialog.allocations },
      pendingAllocations: function (state) { return state.allocationDialog.pendingAllocations },
      tabData: function (state) { return state.allocationDialog.tabData },
      activeTab: function (state) { return state.allocationDialog.activeTab },
      filters: function (state) { return state.allocationDialog.filters },
      selectedAvailableId: function (state) { return state.allocationDialog.selectedAvailableId },
      selectedAllocationId: function (state) { return state.allocationDialog.selectedAllocationId },
      allocateAmount: function (state) { return state.allocationDialog.allocateAmount },
      loading: function (state) { return state.allocationDialog.loading },
      tabLoading: function (state) { return state.allocationDialog.tabLoading },
      saving: function (state) { return state.allocationDialog.saving },
      undoing: function (state) { return state.allocationDialog.undoing },
      error: function (state) { return state.allocationDialog.error },
      fatalError: function (state) { return state.allocationDialog.fatalError }
    }),
    mapGetters([
      'liveTargetRemaining',
      'liveSourceRemaining',
      'isEditMode',
      'currentAllocationAmount',
      'availableItems',
      'maxAllocateAmount',
      'allocationError',
      'isAllocateDisabled',
      'tabRequestBody',
      'tabCurrentPage',
      'tabTotalCount',
      'tabPageSize',
      'editAvailableTabs',
      'filteredAllocations'
    ]),
    {
      isManualAllocationsEdit: function () {
        return this.$route && this.$route.name === 'manualAllocationsEdit'
      },

      transactionId: function () {
        return this.$route.params.transactionId
      },

      editCustomerReference: function () {
        if (this.$route && this.$route.params && this.$route.params.customerReference) {
          return this.$route.params.customerReference
        }
        return this.manualAllocationsCustomerRef
      },

      editBreadcrumbItems: function () {
        var transactionLabel = this.transaction && (this.transaction.Reference || this.transaction.Id)
          ? (this.transaction.Reference || this.transaction.Id)
          : this.transactionId
        return [
          { text: 'Main Dashboard', to: { name: 'dashboard', params: { customerRef: this.editCustomerReference } } },
          { text: 'Allocation Dashboard', to: { name: 'manualAllocationsDashboard', params: { customerId: this.editCustomerReference } } },
          { text: transactionLabel, active: true }
        ]
      },

      editPageSize: function () {
        return 10
      },

      pagedEditAllocations: function () {
        var start = (this.editPage - 1) * this.editPageSize
        return this.filteredAllocations.slice(start, start + this.editPageSize)
      },

      editShowingText: function () {
        var total = this.filteredAllocations.length
        if (total === 0) {
          return 'Showing 0 to 0 of 0 results'
        }
        var from = ((this.editPage - 1) * this.editPageSize) + 1
        var to = Math.min(this.editPage * this.editPageSize, total)
        return 'Showing ' + from + ' to ' + to + ' of ' + total + ' results'
      },

      editAllocationsSchema: function () {
        return {
          columns: [
            { key: 'FromReference', label: 'Reference', component: 'ReferenceCell' },
            { key: 'Created', label: 'Date', formatter: 'shortDate' },
            { key: 'Amount', label: 'Amount', formatter: 'currency' },
            { key: 'Outstanding', label: 'Outstanding', formatter: 'currency' }
          ],
          actions: [
            { name: 'undo', label: 'Undo', hideLabel: true, icon: 'fas fa-minus-circle', class: 'btn-link text-danger border-0 bg-transparent p-0' }
          ]
        }
      },


      manualAllocationsCustomerRef: function () {
        var routeCustomerRef = this.$route && this.$route.query
          ? (this.$route.query.customerId || this.$route.query.customerRef)
          : null
        var storeCustomerRef = this.$store &&
          this.$store.state &&
          this.$store.state.customers &&
          this.$store.state.customers.customer
          ? this.$store.state.customers.customer.CustomerReference
          : null
        if (this.transaction && this.transaction.CustomerReference) {
          return this.transaction.CustomerReference
        }
        if (this.transaction && this.transaction.CustomerId) {
          return this.transaction.CustomerId
        }
        return routeCustomerRef || storeCustomerRef || null
      },

      breadcrumbItems: function () {
        var self = this
        return [
          { text: 'Home', to: { name: 'landingPage' } },
          { text: 'Main Dashboard', to: { name: 'dashboard', params: { customerRef: self.manualAllocationsCustomerRef } } },
          { text: 'Manual Allocations', to: { name: 'manualAllocationsDashboard', params: { customerId: self.manualAllocationsCustomerRef } } },
          { text: self.transaction && self.transaction.Reference, active: true }
        ]
      },

      hasPendingAllocations: function () {
        return (this.pendingAllocations || []).length > 0
      },

      hasPendingInvalid: function () {
        return (this.pendingAllocations || []).some(function (p) { return p.invalid })
      },

      pendingBySourceId: function () {
        var lookup = {}
        ;(this.pendingAllocations || []).forEach(function (entry) {
          if (entry && entry.FromTransactionId !== null && entry.FromTransactionId !== undefined) {
            lookup[entry.FromTransactionId] = true
          }
        })
        return lookup
      },

      selectedSourceId: function () {
        return this.selectedAvailableId || this.selectedAllocationId
      },

      selectedItem: function () {
        var vm = this
        if (!vm.selectedAvailableId) return null
        var tab = vm.tabData && vm.tabData[vm.activeTab]
        if (!tab || !tab.Transactions) return null
        return tab.Transactions.find(function (t) { return t.Id === vm.selectedAvailableId }) || null
      },

      selectedItemDisplayReference: function () {
        if (!this.selectedItem) return ''
        return this.buildTransactionReference(this.selectedItem)
      },

      selectedItemContextLabel: function () {
        if (!this.selectedItem) return ''
        var typeLabel = this.getTypeSingularLabel(this.activeTab)
        if (typeLabel) {
          return typeLabel + ' · ID ' + this.selectedItem.Id
        }
        return 'Transaction · ID ' + this.selectedItem.Id
      },

      availableItemsForDisplay: function () {
        var vm = this
        return (this.availableItems || []).map(function (item) {
          return Object.assign({}, item, {
            Reference: vm.buildTransactionReference(item)
          })
        })
      },

      allAllocations: function () {
        var vm = this
        var pending = (vm.pendingAllocations || []).map(function (p) {
          var src = vm.findPendingSourceTransaction(p)
          return Object.assign({}, p, {
            _pending: true,
            fromReference: vm.buildTransactionReference(src || p),
            fromDate: src ? src.Date : (p.FromDate || null),
            fromType: p.FromType || (src && src.type) || null
          })
        })
        return pending
      },

      availableItemsSchema: function () {
        var vm = this
        return {
          columns: [
            { key: 'Reference', label: 'Reference', component: 'ReferenceCell' },
            { key: 'Date', label: 'Date', formatter: 'shortDate' },
            { key: 'Value', label: 'Amount', formatter: 'currency' },
            { key: 'RemainingBalance', label: 'Remaining', formatter: 'currency' }
          ],
          options: {
            rowVariant: function (row) {
              if (vm.selectedSourceId && row.Id === vm.selectedSourceId) {
                return 'primary'
              }
              if (vm.pendingBySourceId[row.Id]) {
                return 'warning'
              }
              return null
            }
          },
          actions: [
            { name: 'select', label: 'Select', hideLabel: true, icon: 'fas fa-plus-circle', class: 'btn-link text-success border-0 bg-transparent p-0' }
          ]
        }
      },

      hasFilterDraft: function () {
        var d = this.filterDraft
        return !!(d.reference || d.date || d.amountMin !== null || d.amountMax !== null || d.remainingMin !== null || d.remainingMax !== null)
      },

      tabShowingText: function () {
        var page = this.tabCurrentPage || 1
        var pageSize = this.tabPageSize || 10
        var total = this.tabTotalCount || 0
        var from = ((page - 1) * pageSize) + 1
        var to = Math.min(page * pageSize, total)
        return 'Showing ' + from + ' to ' + to + ' of ' + total + ' results'
      }
    }
  ),

  watch: {
    filters: {
      immediate: true,
      deep: true,
      handler: function (nextFilters) {
        var source = nextFilters || {}
        this.filterDraft = {
          reference: source.reference || '',
          date: source.date || null,
          amountMin: source.amountMin !== undefined ? source.amountMin : null,
          amountMax: source.amountMax !== undefined ? source.amountMax : null,
          remainingMin: source.remainingMin !== undefined ? source.remainingMin : null,
          remainingMax: source.remainingMax !== undefined ? source.remainingMax : null
        }
      }
    }
  },

  methods: {
    getTypeSingularLabel: function (type) {
      return type ? getTypeSingular(type) : ''
    },

    normalizeTypeToken: function (value) {
      if (value === null || value === undefined) return null
      return String(value).toLowerCase()
    },

    findPendingSourceTransaction: function (pending) {
      var vm = this
      if (!pending || !vm.tabData) return null

      var tabKeys = Object.keys(vm.tabData)
      var expectedFromTransactionType = vm.normalizeTypeToken(pending.FromTransactionType)
      var i
      var j

      for (i = 0; i < tabKeys.length; i++) {
        var tab = vm.tabData[tabKeys[i]]
        var transactions = tab && tab.Transactions ? tab.Transactions : []
        for (j = 0; j < transactions.length; j++) {
          var item = transactions[j]
          if (item.Id !== pending.FromTransactionId) continue
          if (!expectedFromTransactionType) return item
          if (vm.normalizeTypeToken(item.BankOrSales) === expectedFromTransactionType) {
            return item
          }
        }
      }

      for (i = 0; i < tabKeys.length; i++) {
        tab = vm.tabData[tabKeys[i]]
        transactions = tab && tab.Transactions ? tab.Transactions : []
        for (j = 0; j < transactions.length; j++) {
          item = transactions[j]
          if (item.Id === pending.FromTransactionId) {
            return item
          }
        }
      }

      return null
    },

    buildTransactionReference: function (transaction) {
      if (!transaction) return ''
      var reference = transaction.Reference || transaction.FromReference
      if (reference) return reference

      var bankOrSales = transaction.BankOrSales || transaction.FromTransactionType
      var type = transaction.Type || transaction.FromType
      var id = transaction.Id || transaction.FromTransactionId

      if (bankOrSales && type && id !== null && id !== undefined && id !== '') {
        return bankOrSales + '/' + type + '/' + id
      }
      if (id !== null && id !== undefined && id !== '') {
        return 'ID ' + id
      }
      return ''
    },

    formatDDMMYYYY: function (dateStr) {
      if (!dateStr) return ''
      var d = new Date(dateStr)
      var day = ('0' + d.getDate()).slice(-2)
      var month = ('0' + (d.getMonth() + 1)).slice(-2)
      var year = d.getFullYear()
      return day + '/' + month + '/' + year
    },

    onClose: function () {
      if (this.manualAllocationsCustomerRef) {
        this.$router.push({ name: 'manualAllocationsDashboard', params: { customerId: this.manualAllocationsCustomerRef } })
      } else {
        this.$router.back()
      }
    },

    onEditTypeSelect: function (type) {
      this.$store.commit('setActiveTab', type)
      this.editPage = 1
    },

    applyEditFilters: function () {
      this.$store.commit('setFilters', {
        reference: this.filterDraft.reference,
        date: this.filterDraft.date,
        amountMin: this.filterDraft.amountMin,
        amountMax: this.filterDraft.amountMax,
        remainingMin: this.filterDraft.remainingMin,
        remainingMax: this.filterDraft.remainingMax
      })
      this.editPage = 1
    },

    clearEditFilters: function () {
      this.$store.commit('resetFilters')
      this.filterDraft = { reference: '', date: null, amountMin: null, amountMax: null, remainingMin: null, remainingMax: null }
      this.editPage = 1
    },

    onEditPageChange: function (page) {
      this.editPage = page
    },

    onUndoRowAction: function (payload) {
      if (!payload || payload.action !== 'undo' || !payload.item) return
      this.undoCandidate = payload.item
      this.$bvModal.show('undo-allocation-modal')
    },

    confirmUndoAllocation: function () {
      if (!this.undoCandidate || !this.undoCandidate.Id) {
        this.$bvModal.hide('undo-allocation-modal')
        return
      }
      var vm = this
      this.$store.dispatch('undoAllocation', {
        allocationId: this.undoCandidate.Id,
        customerReference: this.editCustomerReference
      }).then(function () {
        vm.undoCandidate = null
        vm.$bvModal.hide('undo-allocation-modal')
      })
    },

    onEditCancel: function () {
      this.$router.push({
        name: 'manualAllocationsDashboard',
        params: { customerId: this.editCustomerReference }
      })
    },

    onTypeSelect: function (type) {
      this.$store.commit('setActiveTab', type)
      this.handleTypeSelect(type)
    },

    onTabPageChange: function (page) {
      var type = this.activeTab
      this.$store.commit('setTabPage', { type: type, page: page })
      this.$store.commit('setTabData', { type: type, data: null })
      this.$store.dispatch('fetchTabTransactions', { transactionId: this.transactionId, transactionType: type })
    },

    handleTypeSelect: function (type) {
      if (this.tabData[type]) return
      this.$store.dispatch('fetchTabTransactions', { transactionId: this.transactionId, transactionType: type })
    },

    onAvailableItemSelect: function (payload) {
      var item = payload.item
      this.$store.commit('setSelectedAvailableId', item.Id)
      var pending = this.pendingAllocations.find(function (p) { return p.FromTransactionId === item.Id })
      if (pending) {
        this.$store.commit('setSelectedAllocationId', item.Id)
        this.$store.commit('setAllocateAmount', pending.Amount)
      } else {
        this.$store.commit('setSelectedAllocationId', null)
      }
    },

    onAllocatedItemSelect: function (entry) {
      if (entry._pending) {
        var vm = this
        var pendingType = vm.getPendingEntryType(entry)
        var ensureTabLoaded = Promise.resolve()
        if (pendingType) {
          if (vm.activeTab !== pendingType) {
            vm.$store.commit('setActiveTab', pendingType)
          }
          if (!vm.tabData[pendingType]) {
            ensureTabLoaded = Promise.resolve(vm.$store.dispatch('fetchTabTransactions', {
              transactionId: vm.transactionId,
              transactionType: pendingType
            }))
          }
        }
        return ensureTabLoaded.then(function () {
          vm.selectPendingAllocationForEdit(entry)
        })
      } else {
        this.$store.commit('setSelectedAllocationId', null)
        this.$store.commit('setSelectedAvailableId', null)
      }
      return Promise.resolve()
    },

    onAvailableSortChanged: function (context) {
      if (!this.activeTab) return
      this.$store.commit('setTabSort', {
        type: this.activeTab,
        sortBy: (context && context.sortBy) || 'Value',
        sortDirection: context && context.sortDesc ? 'desc' : 'asc'
      })
      this.$store.commit('setTabPage', { type: this.activeTab, page: 1 })
      this.$store.commit('setTabData', { type: this.activeTab, data: null })
      this.$store.dispatch('fetchTabTransactions', { transactionId: this.transactionId, transactionType: this.activeTab })
    },

    onAmountInput: function (value) {
      this.$store.commit('setAllocateAmount', value)
    },

    onMaxAmount: function () {
      this.$store.commit('setAllocateAmount', this.maxAllocateAmount)
    },

    onAllocate: function () {
      if (this.isEditMode) {
        this.$store.commit('updatePendingAllocation', {
          FromTransactionId: this.selectedAllocationId,
          Amount: Number(this.allocateAmount)
        })
      } else {
        if (!this.selectedItem) return
        this.$store.commit('addPendingAllocation', {
          FromTransactionId: this.selectedItem.Id,
          FromTransactionType: this.selectedItem.BankOrSales,
          FromType: this.activeTab,
          Amount: Number(this.allocateAmount)
        })
      }
    },

    resolveTransactionContext: function (transactionId) {
      var tabs = this.tabData || {}
      var tabKeys = Object.keys(tabs)
      var i
      for (i = 0; i < tabKeys.length; i++) {
        var tabType = tabKeys[i]
        var tab = tabs[tabType]
        var rows = tab && tab.Transactions
        if (!rows || !rows.length) continue
        var match = rows.find(function (t) { return t.Id === transactionId })
        if (match) {
          return {
            reference: this.buildTransactionReference(match),
            date: match.Date || null,
            type: tabType
          }
        }
      }
      return null
    },

    getPendingEntryType: function (entry) {
      if (!entry) return null
      return entry.fromType || entry.FromType || ((this.resolveTransactionContext(entry.FromTransactionId) || {}).type) || null
    },

    selectPendingAllocationForEdit: function (entry) {
      this.$store.commit('setSelectedAllocationId', entry.FromTransactionId)
      var src = this.resolveTransactionContext(entry.FromTransactionId)
      this.$store.commit('setSelectedAvailableId', src ? entry.FromTransactionId : null)
      this.$store.commit('setAllocateAmount', entry.Amount)
    },

    allocationRowClasses: function (entry) {
      return {
        'table-danger': entry.invalid
      }
    },

    onClearSelection: function () {
      this.$store.commit('clearSelection')
    },

    onRemoveAllocation: function (entry) {
      if (!entry._pending) return
      this.$store.commit('removePendingAllocation', entry.FromTransactionId)
      if (entry.FromTransactionId === this.selectedAllocationId) {
        this.$store.commit('clearSelection')
      }
    },

    applyFilters: function () {
      this.$store.commit('setFilters', {
        reference: this.filterDraft.reference,
        date: this.filterDraft.date,
        amountMin: this.filterDraft.amountMin,
        amountMax: this.filterDraft.amountMax,
        remainingMin: this.filterDraft.remainingMin,
        remainingMax: this.filterDraft.remainingMax
      })
      if (this.activeTab) {
        // Invalidate active tab cache so it re-fetches with new filters
        this.$store.commit('setTabPage', { type: this.activeTab, page: 1 })
        this.$store.commit('setTabData', { type: this.activeTab, data: null })
        this.$store.dispatch('fetchTabTransactions', { transactionId: this.transactionId, transactionType: this.activeTab })
      }
    },

    clearFilters: function () {
      this.$store.commit('resetFilters')
      this.filterDraft = { reference: '', date: null, amountMin: null, amountMax: null, remainingMin: null, remainingMax: null }
      if (this.activeTab) {
        this.$store.commit('setTabPage', { type: this.activeTab, page: 1 })
        this.$store.commit('setTabData', { type: this.activeTab, data: null })
        this.$store.dispatch('fetchTabTransactions', { transactionId: this.transactionId, transactionType: this.activeTab })
      }
    },

    onModalSave: function () {
      this.$bvModal.hide('save-allocation-modal')
      this.$store.dispatch('submitAllocations', this.transactionId)
    }
  }
}
</script>

<style scoped>
.manual-allocation-summary-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: #2f6fe4;
  color: #fff;
  font-size: 1.2rem;
}

.allocation-tabs-row {
  /* intentionally no border here — border is on the ul below */
}

.allocation-tabs {
  width: 100%;
  border-bottom: 1px solid #dee2e6;
  border-bottom: none;
  gap: 0.25rem;
}

.allocation-tab-link {
  color: #6c757d;
  font-weight: 500;
  font-size: 0.95rem;
  border: none;
  border-bottom: 3px solid transparent;
  background: none;
  padding-left: 0.25rem;
  padding-right: 0.75rem;
  transition: color 0.15s, border-color 0.15s;
}

.allocation-tab-link:hover {
  color: #2d3748;
  border-bottom-color: #cbd5e0;
  text-decoration: none;
}

.allocation-tab-link.active {
  color: #1a202c;
  font-weight: 600;
  border-bottom-color: #1a202c;
}

::v-deep .pagination .page-item .page-link {
  line-height: 1.25;
  padding: 0.25rem 0.5rem;
}

::v-deep .pagination .page-item:first-child,
::v-deep .pagination .page-item:last-child {
  background-color: transparent;
}

::v-deep .pagination .page-item:first-child .page-link,
::v-deep .pagination .page-item:last-child .page-link {
  background-color: transparent;
  border-color: transparent;
}

::v-deep .filter-panel .el-date-editor.el-input,
::v-deep .filter-panel .el-date-editor.el-input__inner {
  height: calc(1.8125rem + 2px) !important;
}

::v-deep .filter-panel .el-date-editor.el-input .el-input__inner {
  height: calc(1.8125rem + 2px) !important;
  line-height: calc(1.8125rem - 2px) !important;
}
</style>

<!-- Non-scoped block to reliably pierce b-pagination internals -->
<style>
.tab-pagination-wrapper .page-item {
  background-color: transparent !important;
}
.tab-pagination-wrapper .page-item .page-link {
  line-height: 1.25;
  padding: 0.25rem 0.5rem;
}

.manual-allocation-view .filter-panel .el-date-editor--date.el-input,
.manual-allocation-view .filter-panel .el-date-editor--date.el-input .el-input__inner {
  height: calc(1.8125rem + 2px) !important;
}

.manual-allocation-view .filter-panel .el-date-editor--date.el-input .el-input__inner {
  line-height: calc(1.8125rem - 2px) !important;
}

</style>